import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { useLang } from '../../context/LangContext';
import { COLORS } from '../../lib/constants';

const ALL_TIMES = ['09:00','09:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00','16:30','17:00','17:30','18:00','18:30','19:00','19:30','20:00'];

function getDates() {
  const dates = [];
  for (let i = 0; i < 14; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    dates.push(d);
  }
  return dates;
}

const DAYS_AR = ['الأحد','الاثنين','الثلاثاء','الأربعاء','الخميس','الجمعة','السبت'];
const DAYS_EN = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS_AR = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];

export default function BookingPage({ data, onBack, onSuccess }) {
  const { business, service, staff } = data;
  const { profile } = useAuth();
  const { isRtl } = useLang();
  const dates = getDates();
  const [selDate, setSelDate] = useState(0);
  const [selTime, setSelTime] = useState(null);
  const [notes, setNotes] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [clientAddress, setClientAddress] = useState('');
  const [locating, setLocating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [bookingInfo, setBookingInfo] = useState(null);
  const [bookedTimes, setBookedTimes] = useState([]);
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [dayClosed, setDayClosed] = useState(false);
  const [openTime, setOpenTime] = useState(null);
  const [closeTime, setCloseTime] = useState(null);
  const [availabilityError, setAvailabilityError] = useState(false);

  useEffect(() => {
    fetchUnavailableTimes();
  }, [selDate]);

  const fetchUnavailableTimes = async () => {
    setLoadingTimes(true);
    setSelTime(null);
    const d = dates[selDate];
    const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

    // get_booking_availability تراعي مدة الخدمة (تداخل حقيقي مو تطابق تام)
    // وتراعي أوقات عمل النشاط الفعلية بدل قائمة أوقات ثابتة بالكود
    const { data, error } = await supabase.rpc('get_booking_availability', {
      p_business_id: business.id,
      p_staff_id: staff?.id || null,
      p_date: dateStr,
      p_duration_minutes: service.duration_minutes,
    });

    if (error || !data) {
      setBookedTimes([]);
      setBlockedTimes([]);
      setDayClosed(false);
      setOpenTime(null);
      setCloseTime(null);
      setAvailabilityError(true);
    } else {
      setBookedTimes((data.blocked_times || []).map(t => t?.slice(0,5)));
      setBlockedTimes([]);
      setDayClosed(!!data.is_closed);
      setOpenTime(data.open_time ? data.open_time.slice(0,5) : null);
      setCloseTime(data.close_time ? data.close_time.slice(0,5) : null);
      setAvailabilityError(false);
    }
    setLoadingTimes(false);
  };

  const getAvailableTimes = () => {
    if (dayClosed) return [];
    const now = new Date();
    const currentMinutes = selDate === 0 ? now.getHours() * 60 + now.getMinutes() + 30 : -1;
    return ALL_TIMES.filter(t => {
      const [h, m] = t.split(':').map(Number);
      const mins = h * 60 + m;
      if (mins <= currentMinutes) return false;
      if (bookedTimes.includes(t)) return false;
      if (openTime) {
        const [oh, om] = openTime.split(':').map(Number);
        if (mins < oh * 60 + om) return false;
      }
      if (closeTime) {
        const [ch, cm] = closeTime.split(':').map(Number);
        if (mins >= ch * 60 + cm) return false;
      }
      return true;
    });
  };

  const detectLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
          const data = await res.json();
          setClientAddress(data.display_name || `${latitude}, ${longitude}`);
        } catch {
          setClientAddress(`${latitude}, ${longitude}`);
        }
        setLocating(false);
      },
      () => setLocating(false)
    );
  };

  const handleBook = async () => {
    if (!selTime) return;
    setSaving(true);

    // جلب المستخدم الحالي مباشرة
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      alert(isRtl ? 'يرجى تسجيل الدخول أولاً' : 'Please login first');
      setSaving(false);
      return;
    }

    // جلب profile
    const { data: userProfile } = await supabase.from('profiles').select('*').eq('id', user.id).single();

    const d = dates[selDate];
    const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

    // استخدام RPC بدل الإدراج المباشر لمنع تعارض الحجز المزدوج (race condition)
    const { data: rpcResult, error: rpcError } = await supabase.rpc('book_appointment', {
      p_user_id: user.id,
      p_business_id: business.id,
      p_staff_id: (staff && staff.id) ? staff.id : null,
      p_service_id: service.id,
      p_booking_date: dateStr,
      p_booking_time: selTime + ':00',
      p_duration_minutes: service.duration_minutes,
      p_price: service.price,
      p_notes: notes,
      p_is_mobile: isMobile,
      p_client_address: isMobile ? clientAddress : null,
    });

    if (rpcError || !rpcResult || rpcResult.success === false) {
      if (rpcResult && rpcResult.error === 'slot_taken') {
        alert(isRtl ? 'عذراً، تم حجز هذا الوقت للتو من شخص آخر. اختر وقتاً آخر.' : 'Sorry, this slot was just booked by someone else. Please choose another time.');
        fetchUnavailableTimes(); // تحديث قائمة الأوقات المتاحة فوراً
      } else {
        alert(isRtl ? 'حدث خطأ أثناء الحجز، حاول مرة أخرى' : 'An error occurred while booking, please try again');
      }
      setSaving(false);
      return;
    }

    const booking = rpcResult.booking;

    try {
      await supabase.functions.invoke('send-booking-email', {
        body: {
          toEmail: user.email,
          toName: (userProfile && userProfile.full_name) || user.email,
          service: isRtl ? service.name_ar : (service.name_en || service.name_ar),
          staffName: staff ? (isRtl ? staff.name_ar : (staff.name_en || staff.name_ar)) : null,
          date: dateStr,
          time: selTime,
          place: isRtl ? business.name_ar : (business.name_en || business.name_ar),
          address: business.address || (business.district ? `${business.district}، ${business.city}` : business.city),
          mapsLink: business.lat && business.lng ? `https://maps.google.com/?q=${business.lat},${business.lng}` : null,
        }
      });
    } catch(e) {}

    setBookingInfo({ ...booking, date: dateStr, time: selTime });
    setDone(true);
    setSaving(false);
  };

  const availableTimes = getAvailableTimes();

  if (done && bookingInfo) return (
    <div style={{ minHeight: '100vh', background: COLORS.bg, fontFamily: 'Cairo, sans-serif', direction: isRtl ? 'rtl' : 'ltr', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', maxWidth: 400, width: '100%' }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, margin: '0 auto 16px', color: '#fff' }}>✓</div>
        <div style={{ fontSize: 22, fontWeight: 900, color: COLORS.text, marginBottom: 8 }}>{isRtl ? 'تم الحجز بنجاح! 🎉' : 'Booking Confirmed! 🎉'}</div>
        <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 24 }}>{isRtl ? 'سيصلك إيميل تأكيد قريباً' : 'A confirmation email will be sent soon'}</div>

        {/* بيانات الحجز */}
        <div style={{ background: '#fff', borderRadius: 16, padding: 20, marginBottom: 16, border: `1px solid ${COLORS.border}`, textAlign: isRtl ? 'right' : 'left' }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: COLORS.text, marginBottom: 14 }}>{isRtl ? 'تفاصيل الحجز' : 'Booking Details'}</div>
          {[
            { icon: '🏪', label: isRtl ? 'المكان' : 'Place', value: isRtl ? business.name_ar : (business.name_en || business.name_ar) },
            { icon: '✂️', label: isRtl ? 'الخدمة' : 'Service', value: isRtl ? service.name_ar : (service.name_en || service.name_ar) },
            staff && { icon: '👤', label: isRtl ? 'الخبير' : 'Expert', value: isRtl ? staff.name_ar : (staff.name_en || staff.name_ar) },
            { icon: '📅', label: isRtl ? 'التاريخ' : 'Date', value: bookingInfo.date },
            { icon: '🕐', label: isRtl ? 'الوقت' : 'Time', value: bookingInfo.time },
            { icon: '💰', label: isRtl ? 'السعر' : 'Price', value: `${service.price} ${isRtl ? 'ر.س' : 'SAR'}` },
          ].filter(Boolean).map((item, i, arr) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < arr.length-1 ? `1px solid ${COLORS.border}` : 'none' }}>
              <span style={{ fontSize: 13, color: COLORS.textMuted }}>{item.icon} {item.label}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{item.value}</span>
            </div>
          ))}
        </div>

        {/* زر الموقع */}
        {business.lat && business.lng && (
          <a href={`https://maps.google.com/?q=${business.lat},${business.lng}`} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: 12, padding: '12px 0', marginBottom: 10, color: '#2E7D32', fontWeight: 700, fontSize: 14, textDecoration: 'none', fontFamily: 'Cairo, sans-serif' }}>
            📍 {isRtl ? 'افتح الموقع في خرائط جوجل' : 'Open in Google Maps'}
          </a>
        )}

        {/* زر الدعم */}
        <a href="https://wa.me/966500748456" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: 12, padding: '12px 0', marginBottom: 16, color: '#2E7D32', fontWeight: 700, fontSize: 14, textDecoration: 'none', fontFamily: 'Cairo, sans-serif' }}>
          💬 {isRtl ? 'تواصل مع الدعم — JADWILAPP' : 'Contact Support — JADWILAPP'}
        </a>

        <button onClick={onSuccess} style={{ width: '100%', background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, border: 'none', borderRadius: 14, padding: 14, color: '#fff', cursor: 'pointer', fontSize: 15, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
          {isRtl ? 'العودة للرئيسية' : 'Back to Home'}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh', fontFamily: 'Cairo, sans-serif', direction: isRtl ? 'rtl' : 'ltr' }}>
      <div style={{ background: '#fff', padding: '16px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onBack} style={{ background: COLORS.bg, border: 'none', borderRadius: 10, padding: '8px 12px', cursor: 'pointer', fontSize: 14, fontFamily: 'Cairo, sans-serif', fontWeight: 700 }}>
          {isRtl ? '→' : '←'}
        </button>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.text }}>{isRtl ? 'تأكيد الحجز' : 'Confirm Booking'}</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>
            {isRtl ? service.name_ar : (service.name_en || service.name_ar)}
            {staff && ` • ${isRtl ? staff.name_ar : (staff.name_en || staff.name_ar)}`}
          </div>
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {/* ملخص */}
        <div style={{ background: COLORS.primaryLight, borderRadius: 16, padding: 16, marginBottom: 20, border: `1px solid ${COLORS.primaryBorder}` }}>
          {staff && (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${COLORS.primaryBorder}` }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {staff.avatar_url ? <img src={staff.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '👤'}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.primaryDark }}>{isRtl ? staff.name_ar : (staff.name_en || staff.name_ar)}</div>
                {staff.rating > 0 && <div style={{ fontSize: 12, color: '#F59E0B' }}>⭐ {Number(staff.rating).toFixed(1)}</div>}
              </div>
            </div>
          )}
          <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.primaryDark, marginBottom: 6 }}>{isRtl ? service.name_ar : (service.name_en || service.name_ar)}</div>
          <div style={{ display: 'flex', gap: 16 }}>
            <span style={{ fontSize: 13, color: COLORS.primary }}>💰 {service.price} {isRtl ? 'ر.س' : 'SAR'}</span>
            <span style={{ fontSize: 13, color: COLORS.primary }}>⏱ {service.duration_minutes} {isRtl ? 'دقيقة' : 'min'}</span>
          </div>
        </div>

        {/* التاريخ */}
        <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, marginBottom: 10 }}>{isRtl ? 'اختر التاريخ' : 'Select Date'}</div>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8, marginBottom: 20 }}>
          {dates.map((d, i) => (
            <button key={i} onClick={() => setSelDate(i)} style={{ flexShrink: 0, width: 56, padding: '10px 0', borderRadius: 14, border: `1.5px solid ${selDate===i ? COLORS.primary : COLORS.border}`, background: selDate===i ? COLORS.primary : '#fff', color: selDate===i ? '#fff' : COLORS.text, cursor: 'pointer', textAlign: 'center', fontFamily: 'Cairo, sans-serif' }}>
              <div style={{ fontSize: 10, marginBottom: 3, opacity: 0.8 }}>{isRtl ? DAYS_AR[d.getDay()] : DAYS_EN[d.getDay()]}</div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>{d.getDate()}</div>
              <div style={{ fontSize: 9, opacity: 0.8 }}>{isRtl ? MONTHS_AR[d.getMonth()] : d.toLocaleString('en', { month: 'short' })}</div>
            </button>
          ))}
        </div>

        {/* الوقت */}
        <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, marginBottom: 10 }}>{isRtl ? 'اختر الوقت' : 'Select Time'}</div>
        {loadingTimes ? (
          <div style={{ textAlign: 'center', padding: 20, color: COLORS.textMuted }}>{isRtl ? 'جارٍ تحميل الأوقات...' : 'Loading times...'}</div>
        ) : availabilityError ? (
          <div style={{ background: '#FEF2F2', borderRadius: 12, padding: 14, marginBottom: 20, fontSize: 13, color: COLORS.error, textAlign: 'center' }}>
            {isRtl ? '⚠️ تعذر تحميل الأوقات المتاحة، ' : '⚠️ Could not load available times, '}
            <button onClick={fetchUnavailableTimes} style={{ background: 'none', border: 'none', color: COLORS.error, textDecoration: 'underline', cursor: 'pointer', fontWeight: 700, fontFamily: 'Cairo, sans-serif', fontSize: 13, padding: 0 }}>
              {isRtl ? 'أعد المحاولة' : 'retry'}
            </button>
          </div>
        ) : dayClosed ? (
          <div style={{ background: '#FEF2F2', borderRadius: 12, padding: 14, marginBottom: 20, fontSize: 13, color: COLORS.error, textAlign: 'center' }}>
            {isRtl ? '🚫 النشاط مغلق هذا اليوم — اختر يوماً آخر' : '🚫 Closed on this day — choose another day'}
          </div>
        ) : availableTimes.length === 0 ? (
          <div style={{ background: '#FEF2F2', borderRadius: 12, padding: 14, marginBottom: 20, fontSize: 13, color: COLORS.error, textAlign: 'center' }}>
            {isRtl ? 'لا تتوفر أوقات — اختر يوماً آخر' : 'No available times — choose another day'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
            {availableTimes.map(t => (
              <button key={t} onClick={() => setSelTime(t)} style={{ padding: '8px 14px', borderRadius: 10, border: `1.5px solid ${selTime===t ? COLORS.primary : COLORS.border}`, background: selTime===t ? COLORS.primary : '#fff', color: selTime===t ? '#fff' : COLORS.text, cursor: 'pointer', fontSize: 13, fontFamily: 'Cairo, sans-serif', fontWeight: 600 }}>
                {t}
              </button>
            ))}
          </div>
        )}

        {/* خدمة متنقلة */}
        {(business.type === 'mobile' || business.type === 'hybrid') && (
          <div style={{ background: '#fff', borderRadius: 14, padding: 14, marginBottom: 16, border: `1px solid ${COLORS.border}` }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: isMobile ? 12 : 0 }}>
              <input type="checkbox" checked={isMobile} onChange={e => setIsMobile(e.target.checked)} style={{ width: 18, height: 18 }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>🚗 {isRtl ? 'أريد الخدمة في موقعي' : 'I want mobile service'}</span>
            </label>
            {isMobile && (
              <div>
                <input value={clientAddress} onChange={e => setClientAddress(e.target.value)} placeholder={isRtl ? 'أدخل عنوانك...' : 'Enter your address...'} style={{ width: '100%', padding: '10px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 10, fontSize: 13, fontFamily: 'Cairo, sans-serif', marginBottom: 8, boxSizing: 'border-box', direction: isRtl ? 'rtl' : 'ltr', outline: 'none' }} />
                <button onClick={detectLocation} disabled={locating} style={{ width: '100%', padding: '10px 0', background: COLORS.primaryLight, border: `1px solid ${COLORS.primaryBorder}`, borderRadius: 10, color: COLORS.primary, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                  {locating ? (isRtl ? 'جارٍ التحديد...' : 'Detecting...') : (isRtl ? '📍 تحديد موقعي تلقائياً' : '📍 Auto-detect my location')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ملاحظات */}
        <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>{isRtl ? 'ملاحظات (اختياري)' : 'Notes (optional)'}</div>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder={isRtl ? 'أي طلبات خاصة...' : 'Any special requests...'} style={{ width: '100%', padding: '12px 14px', border: `1px solid ${COLORS.border}`, borderRadius: 12, fontSize: 13, fontFamily: 'Cairo, sans-serif', direction: isRtl ? 'rtl' : 'ltr', outline: 'none', resize: 'none', height: 80, boxSizing: 'border-box', marginBottom: 20 }} />

        <button onClick={handleBook} disabled={!selTime || saving} style={{ width: '100%', padding: 15, background: !selTime ? COLORS.border : `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, border: 'none', borderRadius: 14, fontSize: 15, fontWeight: 700, color: !selTime ? COLORS.textMuted : '#fff', cursor: !selTime ? 'not-allowed' : 'pointer', fontFamily: 'Cairo, sans-serif' }}>
          {saving ? (isRtl ? 'جارٍ الحجز...' : 'Booking...') : (isRtl ? 'تأكيد الحجز ✓' : 'Confirm Booking ✓')}
        </button>
        <div style={{ height: 20 }} />
      </div>
    </div>
  );
}
